import { Col, DatePicker, Form, Input, Row, Space } from "antd";
import TextArea from "antd/es/input/TextArea";
import modal from "antd/es/modal";
import { useForm } from "antd/lib/form/Form";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DraggableModal } from "../../../components/DraggableModal";
import { MapAddSingleMarker } from "../../../components/maps/MapAddPoint";
import {
  SelectRealEstateConditions,
  SelectTypeEstatePurposeObject,
  SelectTypeObject,
  SelectUnitMeasurement
} from "../../../components/selects";
import SelectGovernment from "../../../components/selects/SelectGovernment";
import SelectOwnerShipTypes from "../../../components/selects/SelectOwnerShipTypes";
import { GeoJsonPoint, Geometry } from "../../../models/geoJsonPoint";
import { EntRealEstate } from "../../../models/realEstate";
import { dateFormat } from "../../../utils";
import dayjs from "dayjs";
import SelectTerritorialUnitsCode from "../../../components/selects/SelectTerritorialUnitsCode";

enum FormField {
  id, // ID реестра недвижимого имущества (обязательно)
  codeEni, // Код ЕНИ (необязательно)
  regionId,
  areaId,
  territorialUnit,
  street, // Улица (необязательно)
  houseNumber, // Номер дома (необязательно)
  longitude, // JSON адрес (обязательно)
  latitude, // JSON адрес (обязательно)
  square, // Площадь (обязательно)
  unitMeasurementId, // ID единицы измерения (обязательно)
  typeObjectId, // ID типа объекта (обязательно)
  purposeObjectId, // ID назначения объекта (обязательно)
  ownershipTypeId, // ID формы собственности (обязательно)
  conditionId, // ID состояния объекта (обязательно)
  description, // Описание (необязательно)
  governmentId, // ID государственного органа (обязательно)
  periodStart,
  periodEnd
}

interface IProps {
  show: boolean;
  realEstate: EntRealEstate;
  onHide: () => void;
  onSubmit: (pos: EntRealEstate) => Promise<boolean>;
}

export const AddRealEstateModal = (props: IProps) => {
  const { t } = useTranslation();
  const [form] = useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [geoJsonData, setGeoJsonData] = useState<GeoJsonPoint|null>(null);
  const [startDate, setStartDate] = useState(null);
  const [selectedRegionId, setSelectedRegionId] = useState<number|null>(null);
  const [selectedRegionPolygonJson, setSelectedRegionPolygonJson] = useState<string|null>(null);
  const [selectedRegionCode, setSelectedRegionCode] = useState<string|null>(null);

  const [selectedAreaCode, setSelectedAreaCode] = useState<string|null>(null);
  const [selectedAreaId, setSelectedAreaId] = useState<number|null>(null);

  useEffect(() => {
	if (props.show) setFieldsFromEntity();
	setSelectedRegionId(props.realEstate.territorialUnitRegion?.id || null);
	setSelectedAreaId(props.realEstate.territorialUnitArea?.id || null);
	setSelectedAreaCode(props.realEstate.territorialUnitArea?.code || null);
  }, [props.show]);

  const setFieldsFromEntity = (): void => {
	form.setFields([
	  {
		name: FormField.periodStart,
		value: props.realEstate.periodStart === null ? undefined : dayjs(props.realEstate.periodStart)
	  },
	  {
		name: FormField.periodEnd,
		value: props.realEstate.periodEnd === null ? undefined : dayjs(props.realEstate.periodEnd)
	  },
	  { name: FormField.codeEni, value: props.realEstate.codeEni },
	  { name: FormField.regionId, value: props.realEstate.territorialUnitRegion?.id },
	  { name: FormField.areaId, value: props.realEstate.territorialUnitArea?.id },
	  { name: FormField.territorialUnit, value: props.realEstate.territorialUnit?.id },
	  { name: FormField.street, value: props.realEstate.street },
	  { name: FormField.houseNumber, value: props.realEstate.houseNumber },
	  { name: FormField.square, value: props.realEstate.square },
	  { name: FormField.unitMeasurementId, value: props.realEstate.unitMeasurement?.id },
	  { name: FormField.typeObjectId, value: props.realEstate.typeObject?.id },
	  { name: FormField.purposeObjectId, value: props.realEstate.purposeObject?.id },
	  { name: FormField.ownershipTypeId, value: props.realEstate.ownershipType?.id },
	  { name: FormField.conditionId, value: props.realEstate.condition?.id },
	  { name: FormField.description, value: props.realEstate.description },
	  { name: FormField.governmentId, value: props.realEstate.government?.id },

	]);
  };

  const updateGeoJsonPoint = () => {
	const latitude = props.realEstate.latitude;
	const longitude = props.realEstate.longitude;

	if (latitude !== undefined && longitude !== undefined) {
	  const geoJsonPoint = new GeoJsonPoint();
	  geoJsonPoint.geometry = new Geometry();
	  geoJsonPoint.geometry.coordinates = [longitude, latitude];
	  setGeoJsonData(geoJsonPoint);
	} else {
	  console.error(t('breeding_farms.error_geometry'));
	}
  };

  useEffect(() => {
	if (props.realEstate.latitude && props.realEstate.longitude) {
	  updateGeoJsonPoint();
	} else {
	  setGeoJsonData(null);
	}
  }, [props.realEstate.latitude, props.realEstate.longitude]);

  const setEntityFromFields = (): EntRealEstate => {
	let res = _.cloneDeep(props.realEstate);
	res.codeEni = form.getFieldValue(FormField.codeEni);
	res.periodStart = form.getFieldValue(FormField.periodStart);
	res.periodEnd = form.getFieldValue(FormField.periodEnd);
	res.regionId = form.getFieldValue(FormField.regionId);
	res.areaId = form.getFieldValue(FormField.areaId);
	res.territorialUnitId = form.getFieldValue(FormField.territorialUnit);
	res.street = form.getFieldValue(FormField.street);
	res.houseNumber = form.getFieldValue(FormField.houseNumber);
	res.square = form.getFieldValue(FormField.square);
	res.unitMeasurementId = form.getFieldValue(FormField.unitMeasurementId);
	res.typeObjectId = form.getFieldValue(FormField.typeObjectId);
	res.purposeObjectId = form.getFieldValue(FormField.purposeObjectId);
	res.ownershipTypeId = form.getFieldValue(FormField.ownershipTypeId);
	res.conditionId = form.getFieldValue(FormField.conditionId);
	res.description = form.getFieldValue(FormField.description);
	res.governmentId = form.getFieldValue(FormField.governmentId);
	if (geoJsonData !== null) {
	  res.latitude = geoJsonData.geometry?.coordinates[1] as number;
	  res.longitude = geoJsonData.geometry?.coordinates[0] as number;
	}
	return res;
  };

  const hideModal = () => {
	props.onHide();
	setSelectedRegionId(null);
	setSelectedRegionPolygonJson(null);
	setSelectedAreaCode(null);
	setGeoJsonData(null);
  };

  const handleSubmit = async () => {
	setLoading(true);
	try {
	  await form.validateFields();
	  if (geoJsonData !== null) {
		const data = setEntityFromFields();
		const result = await props.onSubmit(data);
		if (result) hideModal();
	  } else {
		modal.error({ title: t('msg.error'), content: t('breeding_farms.choose_location') });
	  }
	} finally {
	  setLoading(false);
	}
  };
  const handleStartDateChange = (date: any) => {
	setStartDate(date);
  };

  const disableEndDate = (current: any) => {
	return current && current.isBefore(startDate, 'day');
  };

  return (
	<>
	  <DraggableModal
		title={ props.realEstate.id === null ? t("general.add") : t('general.edit') }
		visible={ props.show }
		onCancel={ () => {
		  hideModal()
		} }
		onSubmit={ handleSubmit }
		submitLoading={ loading }
		width={ 800 }
	  >
		<Form
		  initialValues={ { remember: true } }
		  autoComplete="off"
		  layout="vertical"
		  form={ form }
		>
		  <Row style={ { margin: 0 } } gutter={ 16 }>
			<Col className="gutter-row" span={ 12 }>
			  <div className="block_item_form">
				<Form.Item
				  label={ t('mts.name_organizations') }
				  name={ FormField.governmentId }
				  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
				>
				  <SelectGovernment/>
				</Form.Item>
			  </div>
			  <Form.Item
				label={ t('Код ЕНИ') }
				name={ FormField.codeEni }
				rules={ [
				  { required: true, message: t("msg.fill_in_the_field") },
				  { max: 18, message: t("msg.fill_in_the_field") },
				  {
					validator: (_, value) => {
					  if (!value) return Promise.resolve();
					  if (!/^\d{1,18}$/.test(value)) {
						return Promise.reject(new Error(t("msg.fill_in_the_field")));
					  }
					  return Promise.resolve();
					},
				  },
				] }
			  >
				<Input
				  maxLength={ 18 }
				  showCount
				  autoComplete="off"
				  type="text"
				  onInput={ (e) => {
					const input = e.target as HTMLInputElement;
					input.value = input.value.replace(/\D/g, '').slice(0, 18);
				  } }
				/>
			  </Form.Item>
			  <Form.Item
				label={ t('titles.territorial_unit_area') }
				name={ FormField.regionId }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			  >
				<SelectTerritorialUnitsCode
				  parentId={ 1 }
				  onChange={ (value, option: any) => {
					setSelectedRegionId(Number(value));
					setSelectedRegionPolygonJson(option?.data?.polygonJson);
					setSelectedRegionCode(option?.data?.code);
					setSelectedAreaCode(null);
					setSelectedAreaId(null);
					form.setFieldsValue({
					  [FormField.regionId]: value,
					  [FormField.areaId]: null, // Сбрасываем район
					  [FormField.territorialUnit]: null, // Сбрасываем населенный пункт
					});
				  } }
				/>
			  </Form.Item>
				<>
				  { selectedRegionId || props.realEstate.territorialUnitRegion ? (
					<Form.Item
					  label={ t('titles.territorialUnitDistricts') }
					  name={ FormField.areaId }
					  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
					>
					  <SelectTerritorialUnitsCode
						type={ ['AREA'] }
						parentId={ selectedRegionId }
						onChange={ (value, option: any) => {
						  setSelectedRegionPolygonJson(option?.data?.polygonJson);
						  setSelectedAreaCode(option?.data?.code || null);
						  setSelectedAreaId(value as number);
						  form.setFields([{ name: FormField.territorialUnit, value: null }]);
						} }
					  />
					</Form.Item>
				  ) : null }

			  { selectedAreaCode || props.realEstate.territorialUnit ? (
				<Form.Item
				  label={ t("titles.territorialUnitVillage") }
				  name={ FormField.territorialUnit }
				  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
				>
				  <SelectTerritorialUnitsCode areaCode={ selectedAreaCode }/>
				</Form.Item>
			  ) : null }
				</>
			  <Form.Item
				label={ t('Улица') }
				name={ FormField.street }

			  >
				<Input type="text"/>
			  </Form.Item>

			  <Form.Item
				label={ t('Номер дома') }
				name={ FormField.houseNumber }

			  >
				<Input type="text"/>
			  </Form.Item>
			  <Form.Item
				label={ t('Площадь') }
				name={ FormField.square }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }

			  >
				<Input type="number"/>
			  </Form.Item>

			  <Form.Item
				label={ t('titles.units_of_measurement') }
				name={ FormField.unitMeasurementId }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			  >
				<SelectUnitMeasurement
				  measurementCategoryAlias="area"
				/>
			  </Form.Item>
			</Col>
			<Col className="gutter-row" span={ 12 }>
			  <div className="block_item_form">
				<Space.Compact>
				  <Form.Item
					label={ t('general.start_date_period') }
					name={ FormField.periodStart }
					rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
				  >
					<DatePicker onChange={ handleStartDateChange } format={ dateFormat }
								placeholder={ t('general.select_date') }/>
				  </Form.Item>
				  <Form.Item
					label={ t('general.end_date_period') }
					name={ FormField.periodEnd }
					rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
				  >
					<DatePicker disabledDate={ disableEndDate } format={ dateFormat }
								placeholder={ t('general.select_date') }/>
				  </Form.Item>
				</Space.Compact>
			  </div>
			  <Form.Item
				label={ t('Тип объекта ') }
				name={ FormField.typeObjectId }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }

			  >
				<SelectTypeObject/>
			  </Form.Item>
			  <Form.Item
				label={ t('Назначение объекта') }
				name={ FormField.purposeObjectId }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			  >
				<SelectTypeEstatePurposeObject/>
			  </Form.Item>

			  <Form.Item
				label={ t('titles.type_of_ownership') }
				name={ FormField.ownershipTypeId }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }

			  >
				<SelectOwnerShipTypes/>
			  </Form.Item>


			  <Form.Item
				label={ t('Состояния объекта') }
				name={ FormField.conditionId }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }

			  >
				<SelectRealEstateConditions/>
			  </Form.Item>

			  <Form.Item
				label={ t('mechanisation.transport.description') }
				name={ FormField.description }

			  >
				<TextArea rows={ 4 } maxLength={ 1000 } showCount/>
			  </Form.Item>
			</Col>
		  </Row>
		  <MapAddSingleMarker
			polygonJson={ selectedRegionPolygonJson }
			setGeoJsonData={ setGeoJsonData }
			geoJsonData={ geoJsonData }
		  />

		</Form>
	  </DraggableModal>
	</>
  );
};
